import { FC, useEffect, useMemo } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  CalendarIcon,
  CheckCircleIcon,
  ClipboardCopyIcon,
  LocationMarkerIcon,
  TruckIcon,
} from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import { useGiftAuthGate } from '@service/giftStore/components/GiftStoreAuthGate';
import {
  GiftDeliveryForStore,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import {
  giftOrderForStoreDataAtomFamily,
  giftOrderForStoreStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import { useGiftOrderForStoreHook } from '@shared/api/giftOrder/giftOrder.hook';
import { GiftOrderItemType } from '@shared/api/giftOrder/giftOrder.interface';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { BucketImg } from '@shared/components/BucketImg';
import { formatDate } from '@shared/utils/formatDate';
import { formatPrice } from '@shared/utils/formatPrice';
import { useParams } from 'react-router-dom';

import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import { formatDeliveryDate } from '../utils/giftDeliveryDate.util';

const STATUS_STEPS: { key: GiftDeliveryStatus; label: string }[] = [
  { key: GiftDeliveryStatus.AddressSubmitted, label: '주문접수' },
  { key: GiftDeliveryStatus.Preparing, label: '상품준비' },
  { key: GiftDeliveryStatus.Shipping, label: '배송중' },
  { key: GiftDeliveryStatus.Delivered, label: '배송완료' },
];

const getStepIndex = (status: GiftDeliveryStatus): number => {
  switch (status) {
    case GiftDeliveryStatus.WaitingAddress:
    case GiftDeliveryStatus.AddressSubmitted:
      return 0;
    case GiftDeliveryStatus.Preparing:
      return 1;
    case GiftDeliveryStatus.Shipping:
      return 2;
    case GiftDeliveryStatus.Delivered:
      return 3;
    default:
      return -1;
  }
};

const StatusTimeline: FC<{ status: GiftDeliveryStatus }> = ({ status }) => {
  const isCanceled = status === GiftDeliveryStatus.Canceled;
  const currentStep = getStepIndex(status);

  if (isCanceled) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl bg-red-50 py-4">
        <span className="text-sm font-bold text-red-500">주문이 취소되었습니다</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between px-2">
      {STATUS_STEPS.map((step, idx) => {
        const isActive = idx <= currentStep;
        const isCurrent = idx === currentStep;
        return (
          <div key={step.key} className="flex flex-1 flex-col items-center">
            <div className="relative flex w-full items-center justify-center">
              {idx > 0 && (
                <div
                  className={`absolute right-1/2 h-0.5 w-full ${
                    idx <= currentStep ? 'bg-blue-400' : 'bg-gray-200'
                  }`}
                />
              )}
              <div
                className={`relative z-10 flex h-7 w-7 items-center justify-center rounded-full transition-all ${
                  isCurrent
                    ? 'bg-blue-500 shadow-md shadow-blue-200'
                    : isActive
                    ? 'bg-blue-400'
                    : 'bg-gray-200'
                }`}
              >
                {isActive ? (
                  <CheckCircleSolidIcon className="h-4 w-4 text-white" />
                ) : (
                  <div className="h-2 w-2 rounded-full bg-gray-400" />
                )}
              </div>
            </div>
            <span
              className={`mt-1.5 text-[11px] font-medium ${
                isCurrent ? 'text-blue-600' : isActive ? 'text-blue-400' : 'text-gray-400'
              }`}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const DeliveryItemCard: FC<{
  delivery: GiftDeliveryForStore;
  index: number;
  fallbackName?: string;
}> = ({ delivery, index, fallbackName }) => {
  const giftItem = delivery.giftItem;
  const gi = typeof giftItem === 'object' ? (giftItem as GiftItemForStore) : null;
  const itemName = gi?.name || gi?.giftItemInfo?.name || fallbackName || '배송 상품';
  const imageUrl = useMemo(() => {
    if (!gi) return '';
    return gi.image?.url || gi.giftItemInfo?.image?.url || '';
  }, [gi]);

  const { recipient, shipping } = delivery;
  const desiredDateLabel = formatDeliveryDate(delivery.desiredDeliveryDate);

  const handleCopyTracking = (trackingNumber: string) => {
    navigator.clipboard.writeText(trackingNumber);
    alert('운송장 번호가 복사되었습니다.');
  };

  return (
    <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100">
      {/* 상품 정보 헤더 */}
      <div className="flex items-center gap-3 border-b border-gray-50 px-4 py-3">
        <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
          {imageUrl ? (
            <BucketImg
              value={imageUrl}
              size="w200"
              alt={itemName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[9px] text-gray-400">
              No Image
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-800">{itemName}</p>
          <div className="mt-0.5 flex items-center gap-2">
            <span className="text-xs text-gray-400">#{index + 1}</span>
            <span className="text-xs text-gray-400">수량 {delivery.quantity}개</span>
          </div>
        </div>
      </div>

      {/* 배송 상태 타임라인 */}
      <div className="px-4 py-4">
        <StatusTimeline status={delivery.deliveryStatus} />
      </div>

      {/* 배송 상세 정보 */}
      <div className="space-y-0 border-t border-gray-50">
        {/* 운송장 정보 */}
        {shipping?.trackingNumber && (
          <div className="flex items-center justify-between border-b border-gray-50 px-4 py-3">
            <div className="flex items-center gap-2">
              <TruckIcon className="h-4 w-4 text-blue-400" />
              <div>
                <p className="text-[11px] text-gray-400">택배사 / 운송장</p>
                <p className="text-sm font-medium text-gray-700">
                  {shipping.carrier || '택배사'} {shipping.trackingNumber}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleCopyTracking(shipping.trackingNumber)}
              className="flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-600 active:bg-blue-100"
            >
              <ClipboardCopyIcon className="h-3.5 w-3.5" />
              복사
            </button>
          </div>
        )}

        {/* 받으실 날짜 (날짜 지정 브랜드 주문만) */}
        {desiredDateLabel && (
          <div className="flex items-start gap-2 border-b border-gray-50 px-4 py-3">
            <CalendarIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-400" />
            <div>
              <p className="text-[11px] text-gray-400">받으실 날짜</p>
              <p className="text-sm font-bold text-gray-700">{desiredDateLabel}</p>
            </div>
          </div>
        )}

        {/* 수령인 정보 */}
        {recipient && (
          <div className="flex items-start gap-2 border-b border-gray-50 px-4 py-3">
            <LocationMarkerIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-400" />
            <div>
              <p className="text-[11px] text-gray-400">배송지</p>
              <p className="text-sm font-medium text-gray-700">
                {recipient.name} {recipient.phoneNumber}
              </p>
              <p className="mt-0.5 text-xs text-gray-500">
                {recipient.address}
                {recipient.addressDetail ? ` ${recipient.addressDetail}` : ''}
                {recipient.postalCode ? ` (${recipient.postalCode})` : ''}
              </p>
            </div>
          </div>
        )}

        {/* 날짜 정보 */}
        <div className="flex items-center gap-2 px-4 py-3">
          <CheckCircleIcon className="h-4 w-4 text-blue-400" />
          <div className="flex flex-wrap gap-x-4 gap-y-0.5">
            <div>
              <span className="text-[11px] text-gray-400">주문일 </span>
              <span className="text-xs text-gray-600">
                {formatDate(delivery.purchasedAt, { dateSeparater: '.' })}
              </span>
            </div>
            {shipping?.shippedAt && (
              <div>
                <span className="text-[11px] text-gray-400">발송일 </span>
                <span className="text-xs text-gray-600">
                  {formatDate(shipping.shippedAt, { dateSeparater: '.' })}
                </span>
              </div>
            )}
            {shipping?.deliveredAt && (
              <div>
                <span className="text-[11px] text-gray-400">배송완료 </span>
                <span className="text-xs text-gray-600">
                  {formatDate(shipping.deliveredAt, { dateSeparater: '.' })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const GiftOrderDeliveryStatusPage: FC = function GiftOrderDeliveryStatusPage() {
  const { orderId } = useParams<{ orderId: string }>();

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const { storeRole } = useAuth();
  const { requestReauth } = useGiftAuthGate();

  const role = storeRole();
  useGiftOrderForStoreHook(orderId ?? '', role);
  const orderStatus = useRecoilValue(giftOrderForStoreStatusAtomFamily(orderId ?? ''));
  const order = useRecoilValue(giftOrderForStoreDataAtomFamily(orderId ?? ''));

  useEffect(() => {
    setTitle('배송현황');
  }, [setTitle]);

  useEffect(() => {
    if (orderStatus === 'error' || (orderStatus === 'success' && !order)) {
      requestReauth();
    }
  }, [orderStatus, order, requestReauth]);

  const deliveryList = useMemo(() => {
    return (order?.deliveries ?? []) as GiftDeliveryForStore[];
  }, [order]);

  const deliveryOrderDetails = useMemo(() => {
    if (!order) return [];
    return order.orderDetails.filter((d) => d.type === GiftOrderItemType.Delivery);
  }, [order]);

  // 배송상품 결제금액 — 소량주문 추가금은 라인 스냅샷(giftItemEmbed)에 없고
  // paymentByCash에만 합산돼 있어, 결제총액에서 쿠폰형 라인 금액을 빼는 역산으로 구한다
  // (추가금은 쿠폰형에 붙지 않으므로 전액 배송형 몫).
  const totalDeliveryAmount = useMemo(() => {
    if (!order) return 0;
    const lineAmount = (d: typeof order.orderDetails[number]) => {
      const optionDelta = (d.selectedOptions ?? []).reduce(
        (s, o) => s + (o.priceDelta || 0),
        0,
      );
      return (d.giftItemEmbed.priceRetail + optionDelta) * d.quantity;
    };
    const nonDeliveryTotal = order.orderDetails
      .filter((d) => d.type !== GiftOrderItemType.Delivery)
      .reduce((sum, d) => sum + lineAmount(d), 0);
    return Math.max(0, (order.paymentDetail.paymentByCash ?? 0) - nonDeliveryTotal);
  }, [order]);

  const allDelivered = useMemo(() => {
    return (
      deliveryList.length > 0 &&
      deliveryList.every((d) => d.deliveryStatus === GiftDeliveryStatus.Delivered)
    );
  }, [deliveryList]);

  const productSummary = useMemo(() => {
    if (deliveryOrderDetails.length === 0) return '';
    const firstName = deliveryOrderDetails[0]?.giftItemEmbed?.name || '배송 상품';
    if (deliveryOrderDetails.length === 1) return firstName;
    return `${firstName} 외 ${deliveryOrderDetails.length - 1}건`;
  }, [deliveryOrderDetails]);

  if (
    orderStatus === 'loading' ||
    orderStatus === 'idle' ||
    orderStatus === 'error' ||
    !order
  ) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-gray-50 pb-8 pt-4">
      {/* 요약 헤더 */}
      <div className="mx-4 mb-4 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-blue-100">
        <div className="px-4 py-4">
          <p className="text-xs text-gray-400">
            주문번호 {order._id.slice(-8).toUpperCase()}
          </p>
          <p className="mt-1 break-keep text-base font-bold text-gray-800">
            {productSummary}
          </p>
          <div className="mt-2 flex items-center gap-3 text-sm text-gray-500">
            <span>총 {deliveryOrderDetails.reduce((s, d) => s + d.quantity, 0)}개</span>
            <span className="text-gray-300">|</span>
            <span className="font-semibold text-blue-600">
              {formatPrice(totalDeliveryAmount)}원
            </span>
            {!!order?.deliveryFee?.total && (
              <span className="text-xs text-gray-400">
                (배송비 {formatPrice(order.deliveryFee.total)}원 포함)
              </span>
            )}
          </div>

          {allDelivered && (
            <div className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-blue-50 py-2.5">
              <CheckCircleSolidIcon className="h-5 w-5 text-blue-500" />
              <span className="text-sm font-bold text-blue-600">모든 상품 배송 완료</span>
            </div>
          )}
        </div>
      </div>

      {/* 배송 상품 목록 */}
      <div className="mx-4 space-y-3">
        {deliveryList.length > 0
          ? deliveryList.map((delivery, idx) => (
              <DeliveryItemCard
                key={delivery._id}
                delivery={delivery}
                index={idx}
                fallbackName={deliveryOrderDetails[idx]?.giftItemEmbed?.name}
              />
            ))
          : deliveryOrderDetails.map((item, idx) => (
              <div
                key={idx}
                className="overflow-hidden rounded-xl bg-white px-4 py-4 shadow-sm ring-1 ring-gray-100"
              >
                <p className="text-sm font-medium text-gray-700">
                  {item.giftItemEmbed?.name || '배송 상품'}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  수량 {item.quantity}개 · 배송 정보 준비중
                </p>
              </div>
            ))}
      </div>
    </div>
  );
};

export default GiftOrderDeliveryStatusPage;
